import React, { useEffect, useState } from 'react';
import { Button, Col, Container, Form, Row } from 'react-bootstrap';
import { useParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import Datetime from 'react-datetime';
import moment from 'moment';
import {
  DATE_FORMAT,
  dateToString,
  EXTENDED_DATE_TIME_FORMAT,
  REVERSE_DASH_DATE_FORMAT,
} from '../../common/utils/date/Date.utils';
import PageBreadcrumb from '../../common/components/layout/PageBreadcrumb';
import RouterPaths from '../../constants/RouterPaths';
import {
  downloadInvoiceCreditNote,
  downloadInvoicePDF,
  getInvoice,
  getInvoiceTransactions,
  updateInvoice,
} from '../InvoicesActions';
import ComponentCard from '../../common/components/cards/ComponentCard';
import InvoiceTransactionsTable from './InvoiceTransactionsTable';
import { preparePriceFormat } from '../../helpers/preparePriceFormat';

function InvoiceDetailPage() {
  const { id } = useParams();
  const dispatch = useDispatch();
  const [details, setDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [transactions, setTransactions] = useState([]);
  const [txLoading, setTxLoading] = useState(true);
  const [txError, setTxError] = useState(null);

  // Edit form states
  const [creditFacilityStatus, setCreditFacilityStatus] = useState('');
  const [bankTransferDate, setBankTransferDate] = useState(''); // YYYY-MM-DD
  const [bankStatementTransactionId, setBankStatementTransactionId] = useState('');
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [saveSuccess, setSaveSuccess] = useState(false);

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    setError(null);
    getInvoice(id)()
      .then((data) => {
        if (mounted) {
          setDetails(data);
        }
      })
      .catch((e) => {
        if (mounted) {
          setError(e);
        }
      })
      .finally(() => {
        if (mounted) setLoading(false);
      });
    return () => {
      mounted = false;
    };
  }, [id]);

  // initialize form fields when details are loaded
  useEffect(() => {
    if (details) {
      setCreditFacilityStatus(details.creditFacilityStatus || '');
      const existingDate = details.bankTransferDate
        ? String(details.bankTransferDate).slice(0, 10)
        : '';
      setBankTransferDate(existingDate);
      setBankStatementTransactionId(details.bankStatementTransactionId || '');
      setNote(details.note || '');
      setSaveSuccess(false);
      setSaveError(null);
    }
  }, [details]);

  useEffect(() => {
    let mounted = true;
    setTxLoading(true);
    setTxError(null);
    const invoiceNumber = details?.invoiceNumber;
    if (invoiceNumber) {
      dispatch(getInvoiceTransactions(id))
        .then((data) => {
          if (mounted) setTransactions(data || []);
        })
        .catch((e) => {
          if (mounted) setTxError(e);
        })
        .finally(() => {
          if (mounted) setTxLoading(false);
        });
    } else {
      setTxLoading(false);
    }
    return () => {
      mounted = false;
    };
  }, [dispatch, details]);

  const breadcrumbs = [
    { id: 1, type: 'home' },
    { id: 2, type: 'link', text: 'Invoices', url: RouterPaths.INVOICES },
    { id: 3, type: 'span', text: 'Invoice Details' },
  ];

  const handleSave = () => {
    if (!details) return;
    setSaving(true);
    setSaveError(null);
    setSaveSuccess(false);

    const bankTransferDateIso = bankTransferDate
      ? moment(bankTransferDate, REVERSE_DASH_DATE_FORMAT)
          .startOf('day')
          .format(EXTENDED_DATE_TIME_FORMAT)
      : null;

    const body = {
      creditFacilityStatus: creditFacilityStatus || null,
      bankTransferDate: bankTransferDateIso,
      bankStatementTransactionId: bankStatementTransactionId?.trim()
        ? bankStatementTransactionId.trim()
        : null,
      note: note?.trim() ? note.trim() : null,
      // TODO: add version field to update invoice
      version: 0,
    };

    dispatch(updateInvoice(id, body))
      .then((updated) => {
        if (updated) {
          setDetails(updated);
          setSaveSuccess(true);
        } else {
          // fallback: refetch in case BE returns no data
          return getInvoice(id)().then((fresh) => setDetails(fresh));
        }
        return null;
      })
      .catch(() => {
        setSaveError(true);
      })
      .finally(() => setSaving(false));
  };

  const prepareTransactionStatus = () => {
    if (details?.hasCreditNote) return 'REFUNDED';
    return details?.isCreditFacilityInvoice ? details?.creditFacilityStatus : 'PAID';
  };

  return (
    <Container fluid>
      <PageBreadcrumb items={breadcrumbs} pageTitle="Invoice Details" />

      <ComponentCard title="Invoice Details" className="mb-3">
        {loading && <div>Loading invoice...</div>}
        {error && <div className="text-danger">Failed to load invoice details.</div>}
        {!loading && !error && (
          <>
            {!details?.isCreditFacilityInvoice && (
              <div className="text-danger mb-4">
                This invoice is not a credit facility invoice. Editing is disabled.
              </div>
            )}
            {saveError && (
              <div className="text-danger mb-4">Failed to save changes. Please try again.</div>
            )}
            {saveSuccess && (
              <div className="text-success mb-4">Invoice updated successfully.</div>
            )}

            <Form
              onSubmit={(e) => {
                e.preventDefault();
                handleSave();
              }}
            >
              <Row>
                <Col lg={4}>
                  <Form.Group className="mb-3" controlId="invoiceNumber">
                    <Form.Label>Invoice Number</Form.Label>
                    <Form.Control type="text" value={details?.invoiceNumber ?? ''} disabled />
                  </Form.Group>
                </Col>

                <Col lg={4}>
                  <Form.Group className="mb-3" controlId="invoiceTotalAmount">
                    <Form.Label>Total Amount</Form.Label>
                    <Form.Control
                      type="text"
                      value={
                        details?.totalAmountInPence != null
                          ? preparePriceFormat(details.totalAmountInPence, true)
                          : ''
                      }
                      disabled
                    />
                  </Form.Group>
                </Col>

                <Col lg={4}>
                  <Form.Group className="mb-3" controlId="invoiceOrganizationName">
                    <Form.Label>Dealer</Form.Label>
                    <Form.Control
                      type="text"
                      value={details?.organizationName ?? ''}
                      disabled
                    />
                  </Form.Group>
                </Col>
              </Row>

              {!details?.hasCreditNote && (
                <Row>
                  <Col lg={3}>
                    <Form.Group className="mb-3" controlId="invoiceStatus">
                      <Form.Label>Status</Form.Label>
                      <Form.Select
                        value={creditFacilityStatus || ''}
                        disabled={!details?.isCreditFacilityInvoice || saving}
                        onChange={(e) => setCreditFacilityStatus(e.target.value)}
                      >
                        <option value="">Select status</option>
                        <option value="PAID">Paid</option>
                        <option value="UNPAID">Unpaid</option>
                      </Form.Select>
                    </Form.Group>
                  </Col>
                  <Col lg={3}>
                    <Form.Group className="mb-3" controlId="invoiceBankTransferDate">
                      <Form.Label>Bank Transfer Date</Form.Label>
                      <Datetime
                        closeOnSelect
                        timeFormat={false}
                        dateFormat={DATE_FORMAT}
                        inputProps={{
                          placeholder: '',
                          disabled: !details?.isCreditFacilityInvoice || saving,
                          className: 'form-control form-calendar-input',
                        }}
                        value={dateToString(bankTransferDate)}
                        onChange={(option) => {
                          const m = moment(option);
                          if (m.isValid()) {
                            setBankTransferDate(m.format(REVERSE_DASH_DATE_FORMAT));
                          } else {
                            setBankTransferDate('');
                          }
                        }}
                      />
                    </Form.Group>
                  </Col>
                  <Col lg={3}>
                    <Form.Group className="mb-3" controlId="invoiceBankStatementTransactionId">
                      <Form.Label>Bank Statement Transaction ID</Form.Label>
                      <Form.Control
                        type="text"
                        placeholder="Enter bank statement transaction id"
                        value={bankStatementTransactionId}
                        disabled={!details?.isCreditFacilityInvoice || saving}
                        onChange={(e) => setBankStatementTransactionId(e.target.value)}
                      />
                    </Form.Group>
                  </Col>
                  <Col lg={3}>
                    <Form.Group className="mb-3" controlId="invoiceNote">
                      <Form.Label>Note</Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={2}
                        placeholder="Enter note"
                        value={note}
                        disabled={!details?.isCreditFacilityInvoice || saving}
                        onChange={(e) => setNote(e.target.value)}
                        style={{ maxHeight: '100px' }}
                      />
                    </Form.Group>
                  </Col>
                </Row>
              )}

              <div className="d-flex gap-2 mb-3 mt-4">
                <Button
                  type="submit"
                  className="rounded-pill btn-primary"
                  disabled={!details?.isCreditFacilityInvoice || saving}
                >
                  {saving ? 'Saving...' : 'Save changes'}
                </Button>
                <Button
                  type="button"
                  className="rounded-pill btn-soft-primary"
                  onClick={() => id && dispatch(downloadInvoicePDF(id))}
                >
                  Download Invoice PDF
                </Button>
                {details?.hasCreditNote && (
                  <Button
                    type="button"
                    className="rounded-pill btn-soft-primary"
                    onClick={() => id && dispatch(downloadInvoiceCreditNote(id))}
                  >
                    Download Credit Note PDF
                  </Button>
                )}
              </div>
            </Form>
          </>
        )}
      </ComponentCard>

      {txLoading && <div>Loading transactions...</div>}
      {txError && <div className="text-danger">Failed to load invoice transactions.</div>}
      {!txLoading && !txError && (
        <InvoiceTransactionsTable
          items={transactions}
          invoiceStatus={prepareTransactionStatus()}
        />
      )}
    </Container>
  );
}

export default InvoiceDetailPage;
